import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import LoadingScreen from './LoadingScreen';
import { useConnectionStore } from '../../stores/connectionStore';
import { useDbUpdateStore, type GateSnapshot } from '../../stores/dbUpdateStore';
import { formatElapsed, stepText } from '../../utils/databaseUpdateFormat';
import './DatabaseUpdateGate.css';

/**
 * Holds the page until the selected connection's database is at the schema
 * this API build expects. Wraps the start page (`/`) and every MainLayout page,
 * so a fresh sign-in, a reload and a deep link all pass through it; the tablet
 * page wraps Selective Touch the same way.
 *
 * The usual case is one fast call and straight in. When the database is behind,
 * the API updates it in the background and this shows "Updating your database"
 * until it finishes. Everyone signing in to that database meanwhile waits here
 * too: the new build may already read columns the update is adding. If the
 * update fails they may continue (PSI is alerted).
 */
export default function DatabaseUpdateGate({ children, variant = 'page' }: { children: ReactNode; variant?: 'page' | 'tablet' }) {
  const connectionId = useConnectionStore((s) => s.selectedConnection?.connectionId ?? 0);
  const checkedConnection = useDbUpdateStore((s) => s.connectionId);
  const phase = useDbUpdateStore((s) => s.phase);
  const check = useDbUpdateStore((s) => s.check);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (connectionId > 0) check(connectionId);
  }, [connectionId, check]);

  // Answers cached while the database was behind may predate its new columns.
  const previous = useRef(phase);
  useEffect(() => {
    if (previous.current === 'updating' && phase === 'ready') void queryClient.invalidateQueries();
    previous.current = phase;
  }, [phase, queryClient]);

  // No connection: the layout sends the user to the connection picker.
  if (connectionId <= 0) return <>{children}</>;
  if (checkedConnection !== connectionId || phase === 'checking') {
    return variant === 'tablet' ? <div className="tablet-screen" aria-busy="true" /> : <LoadingScreen />;
  }
  if (phase === 'ready') return <>{children}</>;
  return <DatabaseUpdateScreenConnected variant={variant} />;
}

function DatabaseUpdateScreenConnected({ variant }: { variant: 'page' | 'tablet' }) {
  const state = useDbUpdateStore();
  return <DatabaseUpdateScreen {...state} variant={variant} onContinue={state.continueAnyway} onRetry={state.retry} />;
}

type ScreenProps = Omit<GateSnapshot, 'phase'> & {
  phase: 'updating' | 'failed' | GateSnapshot['phase'];
  variant?: 'page' | 'tablet';
  onContinue: () => void;
  onRetry: () => void;
};

export function DatabaseUpdateScreen({ phase, run, startedByOther, slow, lostContact, receivedAt, variant = 'page', onContinue, onRetry }: ScreenProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  const [now, setNow] = useState(() => Date.now());

  // Move focus to the heading so a screen reader announces what is happening.
  useEffect(() => {
    heading.current?.focus();
  }, [phase]);

  useEffect(() => {
    if (phase !== 'updating') return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [phase]);

  const tablet = variant === 'tablet';
  const failed = phase === 'failed';
  const elapsed = (run?.elapsedMs ?? 0) + (receivedAt > 0 ? Math.max(0, now - receivedAt) : 0);
  const stepCount = run?.stepCount ?? 0;
  const step = Math.min(run?.step ?? 0, stepCount);
  const percent = stepCount > 0 ? Math.round((step / stepCount) * 100) : 0;

  return (
    <main className={`db-update-screen${tablet ? ' db-update-screen--tablet' : ''}`}>
      <section className="db-update-card" aria-labelledby="db-update-title">
        <div className={`db-update-icon${failed ? ' db-update-icon--failed' : ''}`} aria-hidden="true">
          <i className={`bi ${failed ? 'bi-exclamation-triangle' : 'bi-database-gear'}`} />
        </div>
        <h1 id="db-update-title" className="db-update-title" tabIndex={-1} ref={heading}>
          {failed ? "The update didn't finish" : 'Updating your database'}
        </h1>

        {!failed && (
          <>
            <p className="db-update-text">
              {tablet
                ? 'Dietech is updating this facility\'s database. Selective Touch opens by itself when it finishes. Please leave this tablet on this screen.'
                : 'Dietech is updating this facility\'s database to the latest version. This usually takes a minute or two. Large databases can take several minutes. Please keep this page open.'}
            </p>
            {startedByOther && (
              <p className="db-update-text">Another user started this update. It will finish on its own.</p>
            )}
            <div
              className="db-update-bar"
              role="progressbar"
              aria-label="Update progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
            >
              <div className="db-update-bar-fill" style={{ width: `${Math.max(percent, 4)}%` }} />
            </div>
            <p className="db-update-step" role="status" aria-live="polite">
              {stepText(run?.step, stepCount)}
            </p>
            <p className="db-update-elapsed">Time so far: {formatElapsed(elapsed)}</p>
            {run?.currentScript && <p className="db-update-detail">Running {run.currentScript}</p>}
            {slow && (
              <div className="db-update-slow">
                <p className="db-update-text" role="status">
                  This is taking longer than usual. You can keep waiting, or continue now. Some screens may not work until it finishes.
                </p>
                <div className="db-update-actions">
                  <Button variant="outlined" onClick={onContinue}>Continue</Button>
                </div>
              </div>
            )}
          </>
        )}

        {failed && (
          <>
            <p className="db-update-text" role="alert">
              {lostContact
                ? 'We lost contact with the server while it was updating this facility\'s database. You can continue, but some screens may not work until the update finishes.'
                : 'PSI has been notified. You can continue, but some screens may not work until it\'s fixed.'}
            </p>
            {run?.firstError && <p className="db-update-detail">{run.firstError}</p>}
            <div className="db-update-actions">
              <Button variant="contained" onClick={onContinue}>Continue</Button>
              <Button variant="outlined" onClick={onRetry}>Try again</Button>
            </div>
          </>
        )}
      </section>
    </main>
  );
}
